import { Component, OnDestroy, OnInit } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  createCsvUploadBeforeUploadHandler,
  getDuplicateCsvHeaders,
  getFileMetadata,
  normalizeCsvHeader,
} from '../../../shared/utils/upload-file.util';
import { AuthService } from '../../../shared/services/auth.service';
import {
  ExportStatus,
  FileMetadata,
  ProcessResponse,
  PublishResponse,
  TargetColumnsConfig,
  UploadStatus,
  ValidateFileResponse,
} from '../../../../API';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { UploadFilesService } from '../../services/upload-files.service';
import type { FileValidationLog, ValidationLogsInput } from '../../models/validation-logs';
import { FileConfig } from '../../models/files';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-repeat-file-upload',
  templateUrl: './repeat-file-upload.component.html',
  styleUrl: './repeat-file-upload.component.scss',
})
export class RepeatFileUploadComponent implements OnInit, OnDestroy {
  validationLogs: ValidationLogsInput = '';
  isValidatingFiles = false;
  filesValidated = false;
  targetColumns: TargetColumnsConfig[] = [];
  headerValidation: Record<
    number,
    | { headersValidated: boolean; matchedHeaders: string[]; missingHeaders: string[]; duplicateHeaders: string[] }
    | undefined
  > = {};
  existingFileConfig: FileConfig[] = [];
  isProcessing = false;
  isPublishing = false;

  destroy$ = new Subject<void>();

  isProcessed: boolean = false;

  constructor(
    private nzMessageService: NzMessageService,
    private authService: AuthService,
    private userManagementService: UserManagementService,
    public uploadFilesService: UploadFilesService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.targetColumns = this.authService.getTargetColumns();
    const fileItem = {
      fileForm: this.createFileForm(),
      headers: [],
      uploadedFileList: [],
    };
    const fileTemplateConfig = this.authService.getFileTemplateConfig();
    this.existingFileConfig = fileTemplateConfig.map((config) => ({ ...config, fileItem }) as FileConfig);
  }

  ngOnInit(): void {
    this.existingFileConfig.forEach((item) => {
      const fileName = item.displayName;
      const description = item.description;

      (item.fileItem.fileForm.get('fileName') as FormControl).setValue(fileName);
      (item.fileItem.fileForm.get('description') as FormControl).setValue(description);
    });

    this.uploadFilesService.listenRealtimeChanges();

    this.uploadFilesService.updatedItem$.pipe(takeUntil(this.destroy$)).subscribe((updatedItem) => {
      if (updatedItem) {
        this.updateItemInList(updatedItem);
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearAllFiles(false);
    this.uploadFilesService.isFileProcessedSubject.next(false);
    this.uploadFilesService.isFileUploadedSubject.next(false);
  }

  createFileForm(): FormGroup {
    return this.fb.group({
      fileName: new FormControl('', [Validators.required]),
      description: new FormControl('', [Validators.required]),
    });
  }

  getBeforeUploadHandler(index: number) {
    return createCsvUploadBeforeUploadHandler(this.nzMessageService, (file, rawFile) => {
      this.uploadFilesService.resetFileProcessingState();
      this.existingFileConfig[index].fileItem!.uploadedFileList = [file];
      this.existingFileConfig[index].fileItem!.file = rawFile;
      this.existingFileConfig[index].fileItem!.nzFile = file;
      this.existingFileConfig[index].fileItem!.s3Key = undefined;
      this.existingFileConfig[index].fileItem!.fileId = undefined;
      this.existingFileConfig[index].fileItem!.uploadStatus = undefined;
      this.existingFileConfig[index].fileItem!.exportStatus = undefined;
      this.headerValidation[index] = {
        headersValidated: false,
        matchedHeaders: [],
        missingHeaders: [],
        duplicateHeaders: [],
      };
      this.filesValidated = false;
      this.validationLogs = '';

      getFileMetadata(rawFile, this.nzMessageService, (headers, count) => {
        this.existingFileConfig[index].fileItem.headers = headers;
        this.existingFileConfig[index].fileItem.recordCount = count;
      });
    });
  }

  getRemoveHandler() {
    return () => {
      this.clearAllFiles();
      return true;
    };
  }

  validateHeaders(index: number): void {
    const existingFileItem = this.existingFileConfig[index];
    const fileConfig = existingFileItem.fileItem;
    if (!fileConfig?.uploadedFileList?.length) {
      this.nzMessageService.warning('Please upload a file first before validating headers.');
      return;
    }

    if (!fileConfig.file) {
      this.nzMessageService.error('File not found. Please re-upload the file.');
      return;
    }

    getFileMetadata(fileConfig.file, this.nzMessageService, (headers) => {
      const targetColumnMapping = this.authService.getTargetColumnMapping();
      const duplicateHeaders = getDuplicateCsvHeaders(headers);
      const uploadedHeadersSet = new Set(headers.map((h) => normalizeCsvHeader(h)));

      const matched: string[] = [];
      const missing: string[] = [];

      this.targetColumns.forEach((field) => {
        if (!field.key) return;

        const expectedHeader = targetColumnMapping[field.key] || field.key;

        if (uploadedHeadersSet.has(normalizeCsvHeader(expectedHeader))) {
          matched.push(field.key);
        } else {
          missing.push(field.key);
        }
      });

      const validationResult = {
        headersValidated: missing.length === 0 && duplicateHeaders.length === 0,
        matchedHeaders: matched,
        missingHeaders: missing,
        duplicateHeaders,
      };
      this.headerValidation[index] = validationResult;
      this.filesValidated = false;

      if (validationResult.headersValidated) {
        this.validationLogs = `${existingFileItem.displayName}: All Required Headers are present`;
        this.nzMessageService.success(`Headers validated successfully for ${existingFileItem.displayName}.`);
      } else if (duplicateHeaders.length) {
        this.validationLogs = `${existingFileItem.displayName}: Duplicate Header(s): ${duplicateHeaders.join(', ')}`;
        this.nzMessageService.warning(`Duplicate header(s): ${duplicateHeaders.join(', ')}`);
      } else {
        this.validationLogs = `${existingFileItem.displayName}: Missing Required Header(s): ${missing.join(', ')}`;
        this.nzMessageService.warning(`Missing ${missing.length} required header(s): ${missing.join(', ')}`);
      }
    });
  }

  isFieldMissing(index: number, fieldKey: string): boolean {
    return this.headerValidation[index]?.missingHeaders.includes(fieldKey) ?? false;
  }

  isFieldMatched(index: number, fieldKey: string): boolean {
    return this.headerValidation[index]?.matchedHeaders.includes(fieldKey) ?? false;
  }

  hasMissingHeaders(index: number): boolean {
    return (this.headerValidation[index]?.missingHeaders.length ?? 0) > 0;
  }

  hasDuplicateHeaders(index: number): boolean {
    return (this.headerValidation[index]?.duplicateHeaders.length ?? 0) > 0;
  }

  getDuplicateHeadersText(index: number): string {
    const duplicateHeaders = this.headerValidation[index]?.duplicateHeaders ?? [];
    return duplicateHeaders.join(', ');
  }

  getMissingHeadersText(index: number): string {
    const missingHeaders = this.headerValidation[index]?.missingHeaders ?? [];
    return missingHeaders.join(', ');
  }

  clearAllFiles(showMessage: boolean = true): void {
    this.existingFileConfig.forEach((fileConfig) => {
      const fileItem = fileConfig.fileItem;
      if (fileItem) {
        fileItem.uploadedFileList = [];
        fileItem.file = undefined;
        fileItem.nzFile = undefined;
        fileItem.s3Key = undefined;
        fileItem.fileId = undefined;
        fileItem.uploadStatus = undefined;
        fileItem.exportStatus = undefined;
      }
    });
    this.headerValidation = {};
    this.validationLogs = '';
    this.filesValidated = false;
    this.uploadFilesService.resetFileProcessingState();
    this.uploadFilesService.isFileUploadedSubject.next(false);
    if (showMessage) {
      this.nzMessageService.success('All files cleared.');
    }
  }

  get hasAnyFile(): boolean {
    return this.existingFileConfig.some((f) => f?.fileItem!.uploadedFileList?.length);
  }

  get canValidateFiles(): boolean {
    if (!this.hasAnyFile) {
      return false;
    }
    return this.existingFileConfig.every((existingFile, index) => {
      if (existingFile.fileItem && existingFile.fileItem.uploadedFileList.length) {
        return this.headerValidation[index]?.headersValidated === true;
      }
      return true;
    });
  }

  get canProcessFiles(): boolean {
    return this.canValidateFiles && this.filesValidated;
  }

  async validateFiles(): Promise<void> {
    if (!this.canValidateFiles) {
      this.nzMessageService.warning('Please upload file(s) and validate headers before validating file contents.');
      return;
    }

    this.isValidatingFiles = true;
    this.filesValidated = false;
    this.validationLogs = 'Validating file contents...';

    try {
      const validationResults: FileValidationLog[] = [];

      for (const existingFile of this.existingFileConfig) {
        if (!(existingFile.fileItem && existingFile.fileItem.uploadedFileList.length)) {
          continue;
        }

        const validationResult = await this.validateExistingFile(existingFile);
        const fileNameWithExtension = existingFile.fileItem.nzFile?.name;

        validationResults.push({ fileName: fileNameWithExtension, result: validationResult });
      }

      this.validationLogs = validationResults;
      this.filesValidated = validationResults.length > 0 && validationResults.every(({ result }) => result.isValid);

      //Mark file uploaded true
      this.uploadFilesService.isFileUploadedSubject.next(true);

      if (this.filesValidated) {
        this.nzMessageService.success('File validation completed.');
      } else {
        this.nzMessageService.warning('File validation completed with issues.');
      }
    } catch (error: unknown) {
      console.error(error);
      this.validationLogs = `Validation failed: ${error instanceof Error ? error.message : error}`;
      this.filesValidated = false;
      this.nzMessageService.error('File validation failed.');
    } finally {
      this.isValidatingFiles = false;
    }
  }

  private async validateExistingFile(fileConfig: FileConfig): Promise<ValidateFileResponse> {
    const fileItem = fileConfig.fileItem;
    if (!(fileItem && fileItem.file)) {
      throw new Error(`File not found for ${fileConfig.displayName}. Please re-upload the file.`);
    }

    const s3Key = await this.uploadFilesService.uploadFileToS3(fileItem.file);
    fileItem.s3Key = s3Key;

    const validationResult = await this.uploadFilesService.validateFile({
      orgId: this.userManagementService.getOrgId(),
      s3Key,
    });

    if (!validationResult) {
      throw new Error(`No validation response returned for ${fileConfig.displayName}.`);
    }

    await this.persistValidationResult(fileConfig, validationResult);

    return validationResult;
  }

  private async persistValidationResult(fileConfig: FileConfig, validationResult: ValidateFileResponse): Promise<void> {
    const fileItem = fileConfig.fileItem;

    try {
      if (!fileItem.fileId) {
        const metadataCreated = (await this.uploadFilesService.createFileMetadata([fileItem])) as FileMetadata[] | void;
        fileItem.fileId = metadataCreated?.[0]?.fileId;
      }

      if (!fileItem.fileId) {
        throw new Error('missing file metadata');
      }

      const didSaveValidationResults = await this.uploadFilesService.updateValidationResults({
        orgId: this.userManagementService.getOrgId(),
        fileId: fileItem.fileId,
        validationResults: JSON.stringify(validationResult),
      });

      if (!didSaveValidationResults) {
        throw new Error('UpdateValidationResults returned success=false');
      }
    } catch (error) {
      console.warn(`Unable to persist validation results for ${fileConfig.displayName}.`, error);
    }
  }

  async processFiles(): Promise<void> {
    try {
      this.isProcessing = true;
      this.uploadFilesService.resetFileProcessingState();

      const fileItemArray = this.existingFileConfig.map((item) => item.fileItem);

      // Step 1: Save Metadata of the file
      const filesWithoutMetadata = fileItemArray.filter((file) => !file.fileId);
      const metadataCreated = filesWithoutMetadata.length
        ? ((await this.uploadFilesService.createFileMetadata(filesWithoutMetadata)) as FileMetadata[])
        : [];

      fileItemArray.forEach((file) => {
        file.fileId = file.fileId || metadataCreated?.find((item) => item.s3Key == file.s3Key)?.fileId;
        file.uploadStatus = UploadStatus.PROCESSING;
      });

      const metadataUpdated = await this.uploadFilesService.updateFileMetadata(fileItemArray);

      //Step 2: Add call for process api ( Encrypt and Hash)
      const processResponse = (await this.uploadFilesService.processFile(fileItemArray)) as ProcessResponse[];

      if (
        fileItemArray.every((item) => item.fileId) &&
        processResponse.length &&
        processResponse.every((item) => item.success)
      ) {
        this.nzMessageService.info('Successfully started the file processing.');
      } else {
        this.nzMessageService.error('An error occurred while processing the file. Please validate the uploaded file');
      }
    } catch (error) {
      console.error(error);
      this.nzMessageService.error('File processing failed, please try again. ');
    } finally {
      this.isProcessing = false;
    }
  }

  async publishFile(): Promise<void> {
    try {
      this.isPublishing = true;

      const fileItemArray = this.existingFileConfig.map((item) => item.fileItem);

      // Step 1: Save Metadata of the file
      const publishResponse = (await this.uploadFilesService.publishFile(fileItemArray)) as PublishResponse[];

      fileItemArray.forEach((file) => {
        publishResponse?.forEach((item) => {
          if (item.success === true) {
            file.exportStatus = ExportStatus.COMPLETED;
          } else {
            file.exportStatus = ExportStatus.FAILED;
          }
        });
      });

      //Step 2: Add call for process api ( Encrypt and Hash)
      const fileProcessed = await this.uploadFilesService.updateFileMetadata(fileItemArray);

      if (publishResponse.length && publishResponse.every((item) => item.success)) {
        this.nzMessageService.success('Successfully published the processed file');
        this.router.navigate(['/files/history']);
      } else {
        this.nzMessageService.error('An error occurred while publishing file. Please try again');
      }
    } catch (error) {
      console.error(error);
      this.nzMessageService.error('An error occurred while publishing file. Please try again');
    } finally {
      this.isPublishing = false;
    }
  }

  updateItemInList(updatedItem: FileMetadata): void {
    const config = this.existingFileConfig.find((config) => config.fileItem.fileId === updatedItem.fileId);

    if (!config) {
      return;
    }

    if (updatedItem.uploadStatus && config.fileItem.uploadStatus !== updatedItem.uploadStatus) {
      this.uploadFilesService.announceStateUpdate(updatedItem);
    }

    Object.assign(config.fileItem, updatedItem);
  }
}
